/**
 * Route guards for authentication and role-based access.
 *
 * REQ D1-AUTH-004: Protected frontend routes redirect agents appropriately.
 */
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
  useAuth,
  useEffectiveAuth,
  usePermissions,
} from "../../contexts/auth";
import { useModuleEnabled } from "../../contexts/modules";
import {
  getViewPreference,
  landingForPreference,
} from "../../lib/viewPreference";
import {
  isBackOfficePreset,
  isCanvasser,
  isOfficeAdmin,
  worksFinanceDesk,
  worksParalegalDesk,
} from "../layout/crmNav";
import { Loader2 } from "lucide-react";

/**
 * #1392: avoid the one-frame 403 flash when an admin switches between persona
 * previews. The ViewSwitcher updates the preview-persona context and the route
 * in two separate stores, so a freshly-mounted destination guard can briefly
 * see the OUTGOING persona's flags and render its 403 before the incoming
 * persona's flags land.
 *
 * While `previewing`, we hold a neutral spinner for a short settle window
 * (150ms) before committing a denial. A real cross-persona transition settles
 * far inside that window, so the transient frame never shows a 403. If the
 * denial PERSISTS past the window it is a genuine #1383 parity denial (e.g. a
 * previewing-paralegal deep-linking to /finance) and the real 403 renders -- a
 * brief neutral spinner first is imperceptible and never wrong.
 *
 * A NON-preview denial (a real agent lacking the flag, previewing === false) is
 * NEVER deferred: it returns "denied" immediately so genuine 403s are instant.
 *
 * Returns "settling" (neutral spinner), "denied" (403), or "ok" (allowed).
 */
function usePreviewSettledDenial(
  blocked: boolean,
  previewing: boolean,
): "settling" | "denied" | "ok" {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!blocked || !previewing) {
      setSettled(false);
      return;
    }
    setSettled(false);
    const id = setTimeout(() => setSettled(true), 150);
    return () => clearTimeout(id);
  }, [blocked, previewing]);

  if (!blocked) return "ok";
  if (!previewing) return "denied"; // real denial -> instant, never deferred
  return settled ? "denied" : "settling";
}

/** Neutral loading spinner shown on a transient preview-settle frame. */
function SettlingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
      <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
    </div>
  );
}

/**
 * Wrap routes that require authentication.
 * Shows a loading spinner while checking auth state.
 * Redirects to /login if not authenticated, preserving the intended destination.
 */
export function ProtectedRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // First-login enforcement (#698): force a set-password step before the CRM.
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }

  return <Outlet />;
}

/**
 * Auth-only guard that does NOT enforce the must_reset_password redirect.
 *
 * Used exclusively for the forced /set-password page (#698): that page is where
 * a must_reset_password user is SENT, so it must be reachable while the flag is
 * still set -- otherwise the ProtectedRoute redirect would loop. Still bounces
 * an unauthenticated visitor to /login.
 */
export function AuthenticatedRoute() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

/**
 * Wrap routes that require admin role.
 * Agents are shown a 403 forbidden page.
 *
 * REQ D1-AUTH-004: Frontend routes for admin dashboard reject agents.
 */
export function AdminRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  // #1383: while an admin previews-as a non-admin persona, the EFFECTIVE role is
  // that persona's (agent), so the admin shell shows the same 403 their real
  // login would -- faithful page parity, not just nav masking.
  const { role: effectiveRole, preset: effectivePreset, previewing } =
    useEffectiveAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // First-login enforcement (#698): force a set-password step before any surface.
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }

  // #1383: while an admin previews-as a non-admin persona, the EFFECTIVE role
  // is that persona's (agent), so the admin shell shows the same 403 their real
  // login would -- faithful page parity, not just nav masking.
  if (effectiveRole === "agent") {
    // #1392: a PREVIEWING admin must never see a 403 here. Two ways they reach
    // this branch on an admin-only route, both resolved by redirecting to the
    // previewed persona's home (never rendering the 403 panel):
    //   1. mid-switch -- the persona-preview flip lands a frame before the
    //      navigation away from /admin/* commits (the flash this issue fixes);
    //   2. a direct load / reload of an admin URL while a preview is persisted
    //      in localStorage (no pending navigation to resolve a spinner).
    // Redirecting (not spinning) handles BOTH without stranding the user. A
    // genuine denial is untouched: a REAL agent (previewing === false) falls
    // through to the Forbidden page below.
    if (previewing) {
      return (
        <Navigate
          to={landingPathForUser(effectiveRole, effectivePreset)}
          replace
        />
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <div className="text-center max-w-md px-4">
          <h1 className="text-4xl font-bold text-[var(--color-text-primary)] mb-2">403</h1>
          <p className="text-lg text-[var(--color-text-secondary)] mb-4">
            Access denied. This area is restricted to admins.
          </p>
          <a
            href="/crm/dashboard"
            className="inline-block px-4 py-2 bg-brand-accent text-white rounded-lg hover:bg-brand-accent-hover transition-colors"
          >
            Go to CRM Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <Outlet />;
}

/** Shared 403 panel for permission-gated surfaces. */
function ForbiddenPanel({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
      <div className="text-center max-w-md px-4">
        <h1 className="text-4xl font-bold text-[var(--color-text-primary)] mb-2">403</h1>
        <p className="text-lg text-[var(--color-text-secondary)] mb-4">{message}</p>
        <a
          href="/crm/dashboard"
          className="inline-block px-4 py-2 bg-brand-accent text-white rounded-lg hover:bg-brand-accent-hover transition-colors"
        >
          Go to CRM Dashboard
        </a>
      </div>
    </div>
  );
}

/**
 * Wrap routes that require a deal-finance permission flag AND the financial
 * module being enabled (D4-AUTH-001). Admins are a superset (their
 * permission_flags are all-true). The surface simply does not appear if the
 * module is off or the preset lacks the flag.
 */
function FlagModuleRoute({
  flag,
  message,
}: {
  flag:
    | "capture_deal"
    | "view_commission"
    | "enter_own_commission"
    | "authorize_commission"
    | "process_payment"
    | "sign_off_deals"
    | "invest_funds"
    | "view_management_dashboards"
    | "view_all_fica";
  message: string;
}) {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const financialOn = useModuleEnabled("financial");
  const location = useLocation();
  // #1392: settle a preview denial by one tick (all hooks unconditional, stable
  // order -- called before the early returns below).
  const decision = usePreviewSettledDenial(
    !financialOn || !has(flag),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return <ForbiddenPanel message={message} />;
  }
  return <Outlet />;
}

/**
 * Like {@link FlagModuleRoute} but admits the user if they hold ANY ONE of the
 * given flags (the financial module must still be on). Used by the combined
 * Finance/Accounts surface (#1506), which is reachable by a Finance capability
 * (process_payment) OR an Accounts capability (invest_funds) OR the combined
 * persona that holds both. Each underlying queue still enforces its own
 * server-side gate, so no capability is widened by admitting the route.
 */
function AnyFlagModuleRoute({
  flags,
  message,
}: {
  flags: Array<
    | "capture_deal"
    | "view_commission"
    | "enter_own_commission"
    | "authorize_commission"
    | "process_payment"
    | "sign_off_deals"
    | "invest_funds"
    | "view_management_dashboards"
  >;
  message: string;
}) {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const financialOn = useModuleEnabled("financial");
  const location = useLocation();
  const decision = usePreviewSettledDenial(
    !financialOn || !flags.some((f) => has(f)),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return <ForbiddenPanel message={message} />;
  }
  return <Outlet />;
}

/**
 * Combined Finance/Accounts surface guard (/finance-accounts) -- #1506.
 *
 * At LWP, Finance and Accounts are the SAME person. The combined workspace is
 * reachable by anyone who holds a Finance capability (process_payment) OR an
 * Accounts capability (invest_funds) -- the combined FINANCE_ACCOUNTS persona
 * holds both. The page composes the existing FinanceQueue + AccountsInvestQueue,
 * each of which keeps its own server-side gate, so a finance-only / accounts-only
 * user only ever sees the section their flags permit.
 */
export function FinanceAccountsRoute() {
  return (
    <AnyFlagModuleRoute
      flags={["process_payment", "invest_funds"]}
      message="The Finance/Accounts workspace is not available to your permission preset."
    />
  );
}

/**
 * FICA payment-readiness surface guard (/crm/finance/fica-outstanding).
 * Gated on the ``view_all_fica`` flag (FINANCE / FINANCE_ACCOUNTS; admins
 * superset), matching the backend ``CanViewAllFica`` gate. The financial
 * module must also be on.
 */
export function FinanceFicaOutstandingRoute() {
  return (
    <FlagModuleRoute
      flag="view_all_fica"
      message="The FICA payment-readiness view is not available to your permission preset."
    />
  );
}

/**
 * Deals-lodged cash-flow feed guard (/crm/finance/deals-lodged). Gated on the
 * ``view_all_fica`` flag (FINANCE / FINANCE_ACCOUNTS; admins superset),
 * matching the backend ``CanViewAllFica`` gate on
 * ``GET /api/v1/otps/deals-lodged/``. The financial module must be on.
 */
export function FinanceDealsLodgedRoute() {
  return (
    <FlagModuleRoute
      flag="view_all_fica"
      message="The deals-lodged cash-flow view is not available to your permission preset."
    />
  );
}

/** Paralegal work-queue surface guard (/legal). */
export function ParalegalRoute() {
  return (
    <FlagModuleRoute
      flag="capture_deal"
      message="Deal files (Written Deals and Deals in Process) are not available to your permission preset."
    />
  );
}

/** Finance action-queue surface guard (/finance). */
export function FinanceRoute() {
  return (
    <FlagModuleRoute
      flag="process_payment"
      message="The finance queue is not available to your permission preset."
    />
  );
}

/**
 * Payslips surface guard (/crm/payslips) -- D5-RBP-003 (#1413, EPIC #1410).
 *
 * Natalie's Finance role runs the pay cycle, so it must reach on-demand payslip
 * generation -- the same surface admins use at /admin/payslips. Payslips live in
 * the admin shell (AdminRoute, role==="admin") today, so a Finance preset
 * (role=agent, process_payment=True) cannot get there. This guard mounts the
 * SAME Payslips component on a Finance-reachable CRM-shell route (the #1153
 * /admin/document-vault, #1299 /crm/users keep-in-shell precedent) gated on
 * process_payment + the financial module -- the SAME flag the backend payslip
 * endpoints (CanGeneratePayslips) already enforce, which is the source of truth.
 * A preset without process_payment (plain agent / paralegal / accounts) gets a
 * 403 panel here and a 403 from the endpoint; no admin role is granted.
 */
export function PayslipsRoute() {
  return (
    <FlagModuleRoute
      flag="process_payment"
      message="Payslips are not available to your permission preset."
    />
  );
}

/**
 * Commission Sheet surface guard (/crm/commission-sheet) -- #2444 (cluster D of
 * the 2026-07-06 Natalie UAT root-cause analysis).
 *
 * The monthly Commission Sheet (#2201, the in-app equivalent of Natalie's
 * commission workbook) lives in the admin shell at /admin/commission-sheet
 * (AdminRoute, role==="admin"), so a FINANCE / FINANCE_ACCOUNTS preset
 * (role=agent, process_payment=True) could not reach it even though the backend
 * gate ALREADY admits them -- CanViewCommissionSheet = process_payment (#2385),
 * which both Finance presets hold. This guard mounts the SAME CommissionSheet
 * component on a Finance-reachable CRM-shell route, exactly like PayslipsRoute /
 * FinanceFicaOutstandingRoute, gated on process_payment + the financial module
 * (the SAME flag the backend enforces, which is the source of truth). A preset
 * without process_payment (plain agent / paralegal / manager / accounts) gets a
 * 403 panel here and a 403 from the endpoint; no admin role is granted and no
 * access is widened.
 */
export function CommissionSheetRoute() {
  return (
    <FlagModuleRoute
      flag="process_payment"
      message="The Commission Sheet is not available to your permission preset."
    />
  );
}

/**
 * Over-rider Schedules keep-in-shell guard (/crm/overrider-schedules) --
 * #2865, the #2444 pattern.
 *
 * The over-rider schedule editor lives in the admin shell at
 * /admin/overrider-schedules (AdminRoute, role==="admin"); this mount lets a
 * FINANCE / FINANCE_ACCOUNTS preset (role=agent, process_payment=True) reach
 * the SAME component without the admin role -- the backend
 * OverriderScheduleViewSet is gated on CanViewCommissionSheet (process_payment,
 * #2385, the SAME flag enforced here), so RBAC is inherited, never widened.
 */
export function OverriderScheduleRoute() {
  return (
    <FlagModuleRoute
      flag="process_payment"
      message="Over-rider schedules are not available to your permission preset."
    />
  );
}

/**
 * Outstanding Deals register keep-in-shell guard (#2463, the #2444 pattern).
 *
 * The finance mount (/crm/outstanding-deals) of the SAME OutstandingDeals
 * component admins reach at /admin/outstanding-deals. Gated on
 * process_payment -- the SAME flag the backend money gate
 * (CanViewCommissionSheet, #2385) enforces, so Finance / FINANCE_ACCOUNTS
 * reach the full money register without the admin role. The backend
 * CanViewOutstandingDealsRegister additionally admits a management-oversight
 * viewer to a server-MASKED register (no money columns), but managers reach
 * their oversight surfaces separately -- this mount is the finance one.
 */
export function OutstandingDealsRoute() {
  return (
    <FlagModuleRoute
      flag="process_payment"
      message="The Outstanding Deals register is not available to your permission preset."
    />
  );
}

/**
 * Market Share keep-in-shell guard (#2680, the #2444 / #2463 pattern).
 *
 * Finance reported it could not reach the registered & written-sales (Market
 * Share) board. The board is mounted admin-shell at /admin/market-share and
 * management-shell at /crm/oversight/market-share; this Finance-reachable mount
 * (/crm/market-share) renders the SAME MarketShare component for the FINANCE /
 * FINANCE_ACCOUNTS preset without the admin role. Gated on process_payment --
 * the same flag the backend CanViewMarketShare (#2680) admits (alongside
 * view_management_dashboards; admins are a superset). The board exposes area
 * counts + sale volumes only (no commission rand), so no money is unmasked. A
 * preset without process_payment (plain agent / paralegal / accounts /
 * office-admin) gets a 403 panel here and a 403 from the endpoint.
 */
export function MarketShareRoute() {
  return (
    <FlagModuleRoute
      flag="process_payment"
      message="Market share is not available to your permission preset."
    />
  );
}

/**
 * Bond Analytics keep-in-shell guard (#4323, the #2680 Market Share pattern).
 *
 * Bonds by Bank is mounted admin-shell at /admin/bond-analytics and
 * management-shell at /crm/oversight/bond-analytics; this Finance-reachable
 * mount (/crm/bond-analytics) renders the SAME BondAnalytics component for the
 * FINANCE / FINANCE_ACCOUNTS preset. Gated on process_payment -- the flag the
 * backend CanViewBondAnalytics admits alongside view_management_dashboards.
 * Bond counts and values per bank only, no commission rand.
 */
export function BondAnalyticsRoute() {
  return (
    <FlagModuleRoute
      flag="process_payment"
      message="Bond analytics is not available to your permission preset."
    />
  );
}

/**
 * Own-commission / leaderboard surface guard (/crm/commission, /crm/leaderboard)
 * — D5-RBP-001 / #1051.
 *
 * These show the agent their OWN money figures (commission cards) and the agent
 * leaderboard. Per Natalie's "what paralegal does NOT require" list (8 June
 * 2026), the value-free back-office presets (paralegal / manager / accounts,
 * view_commission=False) must not reach them by direct URL — they get a 403, not
 * their figures. A standard field agent and Finance (view_commission=True) are
 * UNAFFECTED. Gated on the financial module too (these are financial-package
 * surfaces). The backend (CanViewCommission on the commission/leaderboard
 * endpoints) is the source of truth; this guard just keeps the URL out of reach.
 */
export function CommissionRoute() {
  // The agent leaderboard/own-commission surface. A MANAGER
  // (view_management_dashboards) is admitted alongside view_commission holders
  // (#1411) so management oversees the company leaderboard; the backend masks
  // every rand for them (commission stays hidden). Paralegal / Accounts /
  // Office-Admin (NEITHER flag) are still denied.
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const financialOn = useModuleEnabled("financial");
  const location = useLocation();
  const decision = usePreviewSettledDenial(
    !financialOn ||
      (!has("view_commission") && !has("view_management_dashboards")),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="Commission figures are not visible to your permission preset." />
    );
  }
  return <Outlet />;
}

/**
 * Sales-targets surface guard (/crm/targets) — D5-RBP-001 / #1051.
 *
 * Natalie's list: the value-free presets do not require sales targets. Gated on
 * view_commission (the same money/value gate) but NOT on the financial module,
 * matching the nav item (which has no module gate). A standard agent and Finance
 * keep view_commission=True and are UNAFFECTED.
 */
export function SalesTargetsRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  // #1411: a MANAGER (view_management_dashboards) sees company sales targets
  // (turnover targets, not commission) alongside view_commission holders.
  const decision = usePreviewSettledDenial(
    !has("view_commission") && !has("view_management_dashboards"),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="Sales targets are not visible to your permission preset." />
    );
  }
  return <Outlet />;
}

/** Manager deal/mandate sign-off surface guard (/management) — D5-RBP-001.
 *  Layered visibility: the surface is reachable with the sign_off_deals flag,
 *  but commission/money sections within it are masked separately (the manager
 *  preset has view_commission=False). The backend is the source of truth. */
export function ManagerRoute() {
  return (
    <FlagModuleRoute
      flag="sign_off_deals"
      message="The deal sign-off queue is not available to your permission preset."
    />
  );
}

/** Behind-schedule view guard (/behind-schedule) — D5-ASW-005.
 *  Worked by the paralegal (capture_deal) AND surfaced to management for the
 *  weekly management meeting (sign_off_deals); either flag suffices. The view is
 *  value-free, so no commission flag is required. The backend
 *  (CanViewBehindSchedule) is the source of truth. */
export function BehindScheduleRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const financialOn = useModuleEnabled("financial");
  const location = useLocation();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(
    !financialOn || (!has("capture_deal") && !has("sign_off_deals")),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="The behind-schedule view is not available to your permission preset." />
    );
  }
  return <Outlet />;
}

/** Accounts deposit-to-invest surface guard (/accounts) — D5-RBP-001.
 *  Layered visibility: reachable with the invest_funds flag; commission figures
 *  are never exposed here (the accounts preset has view_commission=False). */
export function AccountsRoute() {
  return (
    <FlagModuleRoute
      flag="invest_funds"
      message="The deposit-to-invest queue is not available to your permission preset."
    />
  );
}

/**
 * Management-oversight dashboards guard (/crm/oversight/*) -- #1411 (EPIC #1410).
 *
 * A MANAGER (view_management_dashboards) oversees the business READ-ONLY: the
 * operational dashboards (Monthly Summary, Deal / Attorney Pipeline, FICA, KPI,
 * Communications) reused as keep-in-shell mounts inside the CRM shell (the #1153
 * /admin/document-vault, #1299 /crm/users precedent) -- the SAME dashboard
 * components, gated on the flag instead of the admin role, so a manager never
 * needs the admin role and never sees a 403. Commission stays masked: this flag
 * is NOT view_commission, and the backend keeps every commission/payout endpoint
 * on its own gate. Admins hold the flag as a superset (they reach these via the
 * admin shell). The backend (IsAdminOrManagementDashboards / has_company_oversight)
 * is the source of truth; this only keeps the URL out of reach for a user lacking
 * the flag.
 */
export function ManagementDashboardsRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  const decision = usePreviewSettledDenial(
    !has("view_management_dashboards"),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="The management oversight dashboards are not available to your permission preset." />
    );
  }
  return <Outlet />;
}

/**
 * Farm-book import guard (/crm/farm-book-import, /admin/farm-book-import) -- #3442.
 *
 * Loading a farm book is office/data work, not selling work: the ADMIN role,
 * the OFFICE ADMIN (``list_properties``) and the MANAGER
 * (``view_management_dashboards``) do it. A plain field agent and a canvasser
 * hold NEITHER flag and are denied here as well as at the API
 * (``CanImportFarmBook`` is the server-side source of truth), so they can
 * never rewrite the farm book from an upload of their own.
 */
export function FarmBookImportRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  const decision = usePreviewSettledDenial(
    !has("list_properties") && !has("view_management_dashboards"),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="Importing a farm book is restricted to admins, office admins and managers." />
    );
  }
  return <Outlet />;
}

/**
 * Conveyancer directory guard (/crm/conveyancers) -- #1025.
 *
 * Field agents do not need the conveyancer (attorney) firm directory: it is a
 * back-office, after-sales tool. Visibility is the ``capture_deal`` flag, which
 * every back-office preset (paralegal / accounts / finance / manager) holds and
 * admins hold as a superset; a plain agent has it False and gets a 403.
 *
 * Unlike the /legal and /finance surfaces, the directory is NOT gated on the
 * financial module -- it is just an address book of conveyancing firms, useful
 * to any back-office user regardless of the financial package. The backend
 * (CanViewConveyancerDirectory) is the source of truth; agents still cannot hit
 * the directory API even if they reach the route by URL.
 */
export function ConveyancerRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(!has("capture_deal"), previewing);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="The conveyancer directory is not available to your permission preset." />
    );
  }
  return <Outlet />;
}

/**
 * Partner directory guard (/crm/partners, /crm/partners/:id) -- D9-EXT-009,
 * #4157. Sits inside ``ConveyancerRoute`` (capture_deal) and additionally
 * refuses the paralegal desk (paralegal + the combined HR/Paralegal seat):
 * Natalie's ruling is that the conveyancer neither adds partner firms nor sees
 * who they are. The backend (``CanViewPartnerDirectory``) is the source of
 * truth; this keeps the direct URL in step with the hidden nav item.
 */
export function PartnersRoute() {
  const { role, preset } = usePermissions();
  const { previewing } = useEffectiveAuth();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(worksParalegalDesk(role, preset), previewing);

  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="The partner directory is not available to your permission preset." />
    );
  }
  return <Outlet />;
}

/**
 * Canvasser-excluded surface guard (/crm/listings, /crm/document-vault) --
 * #2735 / #2738.
 *
 * The CANVASSER is the most restricted persona (farmbook capture only). Two
 * surfaces leak to it by direct URL / search even though its nav hides them:
 * Property Listings (#2735) and the shared company Document Vault (#2738). This
 * guard DENIES the canvasser and admits every other authenticated user --
 * crucially a PLAIN AGENT and the OFFICE-ADMIN (list_properties) keep both.
 *
 * The discriminator is the shared ``isCanvasser`` helper
 * (``isBackOfficePreset(preset) && has("add_contact_numbers")``): a canvasser is
 * ``role=agent`` with preset "canvasser", and ``add_contact_numbers`` is an
 * agent-baseline flag a plain agent ALSO holds -- so the guard gates on
 * CAPABILITY + back-office membership, never the persona string, and never on
 * the ABSENCE of ``list_properties``/``capture_deal`` (a plain agent lacks those
 * too and must keep access). The backend gates are the source of truth; this
 * only keeps the URL out of the canvasser's reach.
 */
export function CanvasserExcludedRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has, preset } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(isCanvasser(preset, has), previewing);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="This area is not available to your permission preset." />
    );
  }
  return <Outlet />;
}

/**
 * Office-admin-excluded surface guard (/crm/kpis) -- #2741.
 *
 * The agent "Monthly Activity" KPI dashboard is a selling-agent activity
 * surface. The OFFICE-ADMIN (a listing publisher / user-manager, NOT a selling
 * agent) has no agent KPIs, so this guard DENIES the office-admin and admits
 * every other authenticated user -- crucially a PLAIN AGENT keeps the KPI
 * dashboard (a plain agent legitimately uses it), as do admins, managers and
 * the other presets.
 *
 * The discriminator is the shared ``isOfficeAdmin`` helper
 * (``isBackOfficePreset(preset) && has("list_properties") && !has("capture_deal")``):
 * a CAPABILITY test, never the persona string, and never keyed on the mere
 * ABSENCE of ``capture_deal`` (a plain agent lacks it too and must keep access).
 * Mirrors ``CanvasserExcludedRoute`` (#2735/#2738). The nav item is already
 * hidden for a back-office preset (``agentBookOnly``); this closes the
 * direct-URL / search hole. The backend is the source of truth.
 */
export function OfficeAdminExcludedRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has, preset } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(isOfficeAdmin(preset, has), previewing);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="This area is not available to your permission preset." />
    );
  }
  return <Outlet />;
}

/**
 * Finance-desk-excluded surface guard -- D4-FBK-4405 (#4405).
 *
 * The finance desk (``finance`` and the combined ``finance_accounts`` seat,
 * ``worksFinanceDesk``) works the money queues, not the deal / listing /
 * document desks or a field agent's own book. Its nav no longer lists Listings,
 * Farm Link Suggestions, FICA Matrix, Messaging, Templates, the Document Vault,
 * Deals to Process, the Legal Queue list or the own-stats pages; this guard
 * closes the matching direct URLs by REDIRECTING the finance desk to its own
 * home (``landingPathForUser`` -- /finance or /finance-accounts), the
 * ``AgentDashboardRoute`` pattern. Every other user (admins, agents, every
 * other preset) passes straight through, unchanged. Gates on the EFFECTIVE
 * role/preset (#1383) so a preview-as-finance admin is bounced exactly as a
 * real finance user would be. Nav + routing only: the server-side read scopes
 * are untouched. Detail pages a deal file links into (``/legal/:otpId``,
 * ``/crm/listings/:id``) are deliberately NOT wrapped.
 */
export function FinanceDeskExcludedRoute() {
  const { isAuthenticated, loading } = useAuth();
  const { role: effectiveRole, preset: effectivePreset } = useEffectiveAuth();
  const location = useLocation();

  if (loading) {
    return <SettlingSpinner />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (worksFinanceDesk(effectiveRole, effectivePreset)) {
    return <Navigate to={landingPathForUser(effectiveRole, effectivePreset)} replace />;
  }
  return <Outlet />;
}

/**
 * Scoped user-management surface guard (/crm/users).
 *
 * The User & Team Management page is reachable by an admin (via /admin/users in
 * the admin shell) AND by a non-admin scoped user-manager of an ONBOARDING
 * rank (Manager rank 3 / Paralegal rank 4 / HR rank 5 — the manage_users
 * holders) via /crm/users inside the CRM shell. The server is authoritative:
 * the backend rank-hierarchy + no-escalation guard (apps/users/hierarchy.py)
 * decides WHICH targets the caller may create / edit / archive, and the
 * user-management endpoints return 403 to anyone outside the onboarding ranks
 * (Finance/Accounts rank 2 and Office Admin rank 6 included). This guard only
 * keeps the URL out of reach for a user who lacks the flag. Admins hold
 * manage_users as a superset.
 */
export function ManageUsersRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(!has("manage_users"), previewing);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="User management is not available to your permission preset." />
    );
  }
  return <Outlet />;
}

/**
 * Agent-compliance roster guard (/crm/compliance/agents).
 *
 * The roster serves TWO audiences, mirroring the nav item's anyFlag in
 * crmNav.ts exactly: HR (manage_compliance) OWNS the module -- upload/verify,
 * reason codes, counter-sign -- and a MANAGER (view_management_dashboards)
 * reaches it READ-ONLY as company oversight. Neither Office Admin nor
 * Paralegal holds manage_compliance, so they do not inherit the roster
 * through this guard. The backend stays authoritative:
 * CanManageOrViewCompliance gates reads, the stricter CanManageCompliance
 * gates writes, and managed_agents_queryset decides WHICH agents are visible
 * -- this guard only keeps the URL consistent with the nav.
 */
export function AgentComplianceRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(
    !(has("manage_compliance") || has("view_management_dashboards")),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="The agent-compliance roster is not available to your permission preset." />
    );
  }
  return <Outlet />;
}

/**
 * HR / Personnel Document Templates guard (/crm/hr-templates,
 * /admin/hr-templates) -- #2813.
 *
 * The restricted area for BLANK personnel/HR contract templates (fixed-term
 * contracts, independent-broker agreements, etc.) is a genuine PER-USER grant
 * (`manage_hr_documents`), never a preset -- per Natalie/Cam's 2026-07-29
 * ruling, "Only Natalie, Cam and optionally the paralegal" may reach it. A
 * plain agent, and every other back-office preset (finance/manager/accounts/
 * office-admin) UNLESS individually granted, gets a 403 panel here -- the
 * page must not even be discoverable, distinct from the general Template
 * Library (`TemplatesRoute`/`capture_deal`, every back-office preset) which
 * agents can read from. Admins pass automatically (superset via
 * `has_perm_flag`). The backend (`CanManageHRDocuments`,
 * `DocumentViewSet.get_queryset` / `_enforce_company_document_gate`) is the
 * source of truth; a non-holder cannot reach the underlying documents even by
 * URL/id-probing.
 */
export function HRTemplatesRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const location = useLocation();
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(
    !has("manage_hr_documents"),
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="Personnel document templates are restricted to authorised staff." />
    );
  }
  return <Outlet />;
}

/**
 * Templates surface guard (/crm/documents) -- D2-DOC-052 (#3968).
 *
 * Field agents use the agency's letter/email templates: browse, preview and
 * generate a document from their OWN listing, deal or farm record (the record
 * scoping is server-enforced). The guard admits a selling agent (any preset
 * that is not a back-office desk -- the plain agent and a custom bundle) and the
 * ``capture_deal`` desks (paralegal / finance / accounts / manager; admins as a
 * superset), gated additionally on the ``documents`` module being enabled. The
 * office admin, HR and the canvasser get the 403 panel, as before. Template
 * create/edit/delete stay admin-only inside the page. The backend
 * (CanAccessTemplates) is the source of truth.
 */
export function TemplatesRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  const { has, preset } = usePermissions();
  const { previewing } = useEffectiveAuth();
  const documentsOn = useModuleEnabled("documents");
  const location = useLocation();
  const mayUseTemplates = has("capture_deal") || !isBackOfficePreset(preset);
  // #1392: settle a preview denial by one tick (hooks unconditional, top-level).
  const decision = usePreviewSettledDenial(
    !documentsOn || !mayUseTemplates,
    previewing,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }
  if (decision === "settling") {
    return <SettlingSpinner />;
  }
  if (decision === "denied") {
    return (
      <ForbiddenPanel message="Document templates are not available to your role." />
    );
  }
  return <Outlet />;
}

/**
 * Route guard that redirects to the appropriate landing based on role + preset.
 * Used for the root "/" path and legacy "/dashboard" paths.
 *
 * Preset-based landing (D4-AUTH-001): a Finance-preset user lands on /finance,
 * a Paralegal-preset user on /legal, admins on /admin, everyone else on /crm.
 * Admins can still reach every surface; the redirect is only the default.
 */
/**
 * Single source of truth for a user's post-login landing path (D4-AUTH-001 +
 * D5-RBP-001). Used by both ``RoleRedirect`` (the index route) AND the login
 * form so a back-office preset (finance/paralegal/manager/accounts) lands on
 * its scoped surface no matter which path triggered the navigation.
 */
export function landingPathForUser(
  role: string | undefined,
  preset: string | null | undefined,
): string {
  if (role === "admin") {
    return "/admin/dashboard";
  }
  switch (preset) {
    // The combined Finance/Accounts persona lands on the combined surface.
    case "finance_accounts":
      return "/finance-accounts";
    case "finance":
      return "/finance";
    // D5-RBP-005 (#4175): the combined HR/Paralegal desk lands on the
    // paralegal workspace; the HR compliance roster is one nav click away.
    case "paralegal":
    case "hr_paralegal":
      // The deals-to-process workspace is the paralegal home.
      return "/paralegal";
    case "manager":
      return "/management";
    case "accounts":
      return "/accounts";
    // The office admin does not hold manage_users, so /crm/users would 403 --
    // land them on the shared CRM dashboard instead (their job is listing admin).
    case "office_admin":
      return "/crm/dashboard";
    // HR owns the agent-compliance roster -- land them on it.
    case "hr":
      return "/crm/compliance/agents";
    // The canvasser works the farmbook -- land them there, not the empty
    // agent dashboard (whose "My Dashboard" nav link is hidden for a back-office
    // preset).
    case "canvasser":
      return "/crm/farm";
    default:
      return "/crm/dashboard";
  }
}

/**
 * Agent-dashboard guard (/crm/dashboard) -- #1267 (admin carve-out #1358).
 *
 * The agent stats dashboard is an agent-activity surface. A back-office
 * permission preset (paralegal/finance/manager/accounts) has its own scoped
 * landing (``landingPathForUser``) and does not need the agent dashboard, so
 * hitting ``/crm/dashboard`` directly bounces them to their real landing (a
 * paralegal -> /paralegal). A plain field agent (whose landing IS
 * /crm/dashboard) is UNAFFECTED and sees the dashboard as before.
 *
 * #1358: ADMINS are explicitly allowed through. An admin legitimately has the
 * agent CRM as a second view (the #1192 ViewSwitcher "CRM" segment + the #1192
 * "/" CRM-preference landing both target /crm/dashboard). Bouncing admins to
 * /admin/dashboard here made the CRM segment round-trip straight back to the
 * admin dashboard -- the admin could never actually reach the CRM view, which
 * is exactly the "CRM button does nothing" report. The #1267 back-office
 * redirect is unchanged for the permission presets.
 *
 * Nav-only counterpart: ``CrmSidebar`` drops "My Dashboard" from the
 * back-office nav (``backOfficeHidden``). This guard closes the direct-URL hole
 * the nav removal opens (acceptance criterion: a paralegal that hits
 * /crm/dashboard directly is redirected to /paralegal).
 */
export function AgentDashboardRoute() {
  const { user, isAuthenticated, loading } = useAuth();
  // #1383: gate on the EFFECTIVE role/preset so a preview-as-paralegal admin is
  // bounced to /paralegal exactly as a real paralegal would be (faithful page
  // parity). When NOT previewing, this is the real admin (allowed through).
  const { role: effectiveRole, preset: effectivePreset } = useEffectiveAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.must_reset_password) {
    return <Navigate to="/set-password" replace />;
  }

  // D8-ANA-W1-009: force the versioned Terms & Conditions acceptance step
  // before the CRM, parallel to (and after) the must_reset_password gate above.
  if (user?.must_accept_terms) {
    return <Navigate to="/accept-terms" replace />;
  }

  // #1358: admins are allowed to view the agent dashboard -- it is their #1192
  // CRM view. Only the back-office permission presets are redirected to their
  // scoped landing (#1267). #1383: a previewing admin uses the simulated
  // role/preset, so preview-as-paralegal bounces to /paralegal.
  const landing = landingPathForUser(effectiveRole, effectivePreset);
  if (effectiveRole !== "admin" && landing !== "/crm/dashboard") {
    return <Navigate to={landing} replace />;
  }
  return <Outlet />;
}

export function RoleRedirect() {
  const { user, isAuthenticated, loading } = useAuth();
  // #1383: when an admin previews-as a persona, "/" lands on that persona's home
  // (a paralegal -> /paralegal) using the simulated role/preset, so the preview
  // never strands the admin on the admin dashboard. Exiting preview restores the
  // real admin landing. When NOT previewing this is the real user, unchanged.
  const { role: effectiveRole, preset: effectivePreset, previewing } =
    useEffectiveAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // While previewing, honour the simulated persona's landing directly (the
  // persisted Admin/CRM view preference is the admin's own and is irrelevant
  // inside a persona preview).
  if (previewing) {
    return (
      <Navigate
        to={landingPathForUser(effectiveRole, effectivePreset)}
        replace
      />
    );
  }

  // Preset-based landing for admin + finance / paralegal / manager / accounts
  // staff (D4-AUTH-001 + D5-RBP-001) -- shared with the login form.
  //
  // #1192: an elevated user (admin / back-office preset) has two views (their
  // own dashboard and the agent CRM). The top-header ViewSwitcher persists the
  // user's last-chosen view; honour it for the index "/" default so a user who
  // chose "CRM view" lands there on reload / next session instead of being
  // bounced back to their home dashboard every time. For a plain agent (home IS
  // /crm/dashboard) the preference is moot and this is a no-op. This governs the
  // "/" default ONLY -- deep links to a specific page are routed by their own
  // route and never hijacked.
  const homePath = landingPathForUser(user?.role, user?.permission_preset);
  return <Navigate to={landingForPreference(homePath, getViewPreference())} replace />;
}
